import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
// @dynamic-i18n-keys
// Dynamic t() at line 104 (common.productCategory.${productType}):
//   common.productCategory.exclusive
//   common.productCategory.exclusive_resale
//   common.productCategory.pcp
//   common.productCategory.premium_gallery
//   common.productCategory.commission
//   common.productCategory.originals
//   common.productCategory.tier
import { PapiStudioSellerStatsInit } from '@wix/da-papi-types';
import { sanitizeHref } from '@wix/da-url';
import { formatDollars } from '@wix/da-shared-react/pkg/utils/formatting';
import ReportsSection from '../ReportsSection';
import ReportsTable from '../ReportsTable';
import { ProductType } from '../../products/types';
import ProductTypeIcon from './ProductTypeIcon';

import s from './SoldProductsSection.scss';

export interface Props {
  className?: string;
  data?: PapiStudioSellerStatsInit['soldProducts'];
}

const SoldProductsSection: FC<Props> = ({ data, className }) => {
  const { t } = useTranslation();
  const columns = useMemo(() => {
    return [
      {
        key: 'productType',
        label: t('studio.reports.section.productSales.table.header.productType'),
      },
      {
        key: 'uniqueProductsSold',
        label: t('studio.reports.section.productSales.table.header.uniqueProductsSold'),
      },
      {
        key: 'totalSales',
        label: t('studio.reports.common.table.header.totalSales'),
      },
      {
        key: 'uniqueCustomers',
        label: t('studio.reports.section.productSales.table.header.uniqueCustomers'),
      },
      {
        key: 'salesVolume',
        label: t('studio.reports.common.table.header.salesVolume'),
      },
    ];
  }, [t]);

  if (!data) {
    return null;
  }

  // If the user has ever sold only subscriptions, this section is not displayed.
  const hasOnlySoldSubscriptions = data?.timePeriods?.ALL?.every(
    item => item.productType === ProductType.Tier
  );
  if (hasOnlySoldSubscriptions) {
    return null;
  }

  return (
    <ReportsSection
      reportType="soldProducts"
      className={className}
      title={t('studio.reports.section.productSales.title')}
      description={t('studio.reports.section.productSales.description', {
        postProcess: ['reactPostprocessor'],
        learnMoreLink: (
          <a
            className="text-link-level-2-tertiary"
            href={sanitizeHref(
              t('studio.reports.section.productSales.description.learnMoreLink.url')
            )}
          >
            {t('studio.reports.section.productSales.description.learnMoreLink.text')}
          </a>
        ),
      })}
      data={data}
      dataFilter={dataToFilter =>
        /* do not shows rows that have no sales */
        dataToFilter?.filter(item => (item.uniqueProductsSold || 0) > 0)
      }
      renderTable={(filteredData: any[]) => (
        <ReportsTable
          columns={columns}
          data={filteredData}
          renderRow={({
            productType,
            uniqueProductsSold,
            totalSales,
            uniqueCustomers,
            salesVolume,
          }: any) => (
            <>
              <td>
                <div className={s['icon-and-productype']}>
                  <ProductTypeIcon productType={productType} size={40} />
                  <div className={s['product-type']}>
                    {t(`common.productCategory.${productType}`)}
                  </div>
                </div>
              </td>
              <td>{uniqueProductsSold}</td>
              <td>{totalSales}</td>
              <td>{uniqueCustomers}</td>
              <td>{formatDollars(salesVolume)}</td>
            </>
          )}
          renderFooter={(totals: any) => {
            return (
              <tr>
                <td>{t('studio.reports.section.common.table.footer.total')}</td>
                <td>{totals?.uniqueProductsSold}</td>
                <td>{totals?.totalSales}</td>
                <td>{totals?.uniqueCustomers}</td>
                <td>{formatDollars(totals?.salesVolume)}</td>
              </tr>
            );
          }}
        />
      )}
    />
  );
};

export default SoldProductsSection;
